import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { createHttpClient } from '../services/http';
import useErrorNavigation from '../lib/composables/useErrorNavigation';
import { useErrorStore } from './useErrorStore';
import * as Sentry from '@sentry/vue';
import { router } from '../router';

export const useApiClientStore = defineStore('api-client', () => {
  const tenantId = ref<string | null>(null);

  // Recomputed whenever tenantId changes
  const client = computed(() => {
    if (!tenantId.value) return null;

    // Grab these inside the computed so the store instance exists
    const errorStore = useErrorStore();
    const errorNavigation = useErrorNavigation(router);

    return createHttpClient(tenantId.value, {
      // NB. all request errors are handled here to keep the rest of the app clean
      onError: async ({ response }) => {
        const next = response.errors; // this should be the array from http.ts
        errorStore.setErrors(next);

        // Navigate to the error page unless explicitly told not to
        if (errorStore.errors[0] && errorStore.errors[0]?.redirectToError !== false) {
          await errorNavigation.navigateToError({
            message: 'Sorry, something went wrong.',
          });
        } else {
          // capture error as not redirecting to the error page
          Sentry.captureMessage(`createHttpClient onError`, {
            level: 'info',
          });
        }
      },
    });
  });

  function setTenantId(id: string | null) {
    tenantId.value = id;
  }

  function getClient() {
    if (!client.value) {
      throw new Error('API client not initialised (tenant not set)');
    }
    return client.value;
  }

  return { tenantId, client, setTenantId, getClient };
});
